import { FC, useContext, useMemo } from 'react'
import { useQuery } from 'react-query'

import {
  QUERIES,
  WithChildren,
  buildQueryString,
  createResponseContext,
  initialQueryResponse,
} from '_metronic/helpers'
import { EsusEnvioListItem } from '../../core/_models'
import { getEsusEnvios } from '../../core/_requests'
import { useQueryRequest } from './QueryRequestProvider'

const QueryResponseContext = createResponseContext<EsusEnvioListItem>(initialQueryResponse)

/** Ordenação padrão: o backend já usa `Sort.by(DESC, "id")` quando o `Pageable` não traz sort. */
const SORT_PADRAO = 'sort=id,desc'

export const QueryResponseProvider: FC<WithChildren> = ({ children }) => {
  const { state } = useQueryRequest()

  const query = useMemo(() => {
    // `buildQueryString` já descarta '', null e undefined — filtro não preenchido simplesmente não
    // sai na query, que é o que faz "Limpar filtros" voltar à listagem sem filtro.
    const parametros = buildQueryString({
      page: state.page,
      size: state.size,
      ...(state.params || {}),
    })
    // O sort é montado à mão porque o separador do Spring é a vírgula literal (`sort=id,desc`) e
    // `buildQueryString` a codificaria junto do valor.
    const sort = state.sort ? `sort=${state.sort},${state.order ?? 'desc'}` : SORT_PADRAO
    return [parametros, sort].filter(Boolean).join('&')
  }, [state])

  // A empresa faz parte da chave porque está dentro de `query` (`state.params.empresaId`) — trocar o
  // filtro de empresa refaz a busca. O escopo de tenant em si é do backend, não da chave de cache.
  const {
    isFetching,
    isError,
    refetch,
    data: response,
  } = useQuery([QUERIES.ESUS_ENVIOS_LIST, query], () => getEsusEnvios(query), {
    cacheTime: 0,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })

  return (
    <QueryResponseContext.Provider
      value={{ isLoading: isFetching, isError, refetch, response, query }}
    >
      {children}
    </QueryResponseContext.Provider>
  )
}

export const useQueryResponse = () => useContext(QueryResponseContext)
